import { useContext, useEffect, useRef, useState } from 'react';
import {
  LogOut,
  Settings,
  User,
  Briefcase,
  ChartSpline,
  ChevronDown,
  HelpCircle,
  FolderGit2,
  Hammer,
  Bookmark,
} from 'lucide-react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { UserContext } from '../../../context/user/UserContext';

function Account({
  setIsMenuOpen,
  loggedIn,
  user,
  loading,
  userDetail,
  handleLogout,
}) {
  const dropdownRef = useRef(null);
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();

  const openSignin = () => navigate('/signin', { state: { background: location } });

  const activeRole = loggedIn ? user?.active_role : null;

  useEffect(() => {
    const handler = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setOpen(false);
      }
    };

    if (open) document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  if (loading) return null;

  return (
    <div className="relative" ref={dropdownRef}>
      {loggedIn ? (
        <>
          {/* Avatar trigger */}
          <button
            type="button"
            onClick={() => {
              setOpen(!open);
              setIsMenuOpen(false);
            }}
            className="
    flex items-center gap-2 rounded-full px-2 py-1
    hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-rose-500
    transition-all duration-200
  "
          >
            {/* Avatar */}
            <div
              className="
      h-9 w-9 rounded-full overflow-hidden
      border border-gray-300 bg-white
      flex items-center justify-center
      text-sm font-semibold text-gray-700
    "
            >
              {userDetail?.profile_image ? (
                <img
                  src={userDetail.profile_image}
                  alt={`${user?.name}'s profile`}
                  className="h-full w-full object-cover"
                />
              ) : (
                user?.name?.[0]?.toUpperCase()
              )}
            </div>

            {/* Name (hidden on small screens like real apps) */}
            <span className="hidden sm:block text-sm font-medium text-gray-800">
              {user?.name}
            </span>

            {/* Chevron */}
            <ChevronDown
              className={`
      h-4 w-4 text-gray-500
      transition-transform duration-200
      ${open ? 'rotate-180' : ''}
    `}
            />
          </button>

          {/* Dropdown */}
          {open && (
            <div
              className="absolute lg:right-0 right-4 w-72 rounded-2xl bg-white
                            shadow-2xl border border-gray-200 animate-scaleIn z-50
                            bottom-full mb-3 lg:bottom-auto
    lg:top-full lg:mt-3"
            >
              {/* Profile header */}
              <div className="flex items-center gap-3 p-4 border-b">
                <div className="w-12 h-12 rounded-full bg-rose-50 flex items-center justify-center overflow-hidden border border-rose-100">
                  {userDetail?.profile_image ? (
                    <img
                      src={userDetail.profile_image}
                      alt="Profile"
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    <span className="text-lg font-black text-rose-500 uppercase">
                      {user?.name?.[0] || 'U'}
                    </span>
                  )}
                </div>

                <div>
                  <h3 className="font-semibold text-gray-900 leading-tight">
                    {user?.name}
                  </h3>
                  <p className="text-xs text-gray-500">{user?.email}</p>
                </div>
              </div>

              {/* Menu */}
              <div className="p-2 space-y-1">
                <MenuItem
                  to={
                    activeRole === 'user' ? '/user/profile' : '/artisan/profile'
                  }
                  icon={<User size={16} />}
                  label="View Profile"
                  onClick={() => setOpen(false)}
                />

                <MenuItem
                  to={
                    activeRole === 'user'
                      ? '/user/my-bookings'
                      : '/artisan/bookings'
                  }
                  icon={<Briefcase size={16} />}
                  label={activeRole === 'user' ? 'My Bookings' : 'Bookings'}
                  onClick={() => setOpen(false)}
                />

                {activeRole === 'artisan' && (
                  <>
                    <MenuItem
                      to="/artisan/project-hub"
                      icon={<FolderGit2 size={16} />}
                      label="Project Hub"
                      onClick={() => setOpen(false)}
                    />
                    <MenuItem
                      to="/artisan/my-bids"
                      icon={<Hammer size={16} />}
                      label="My Bids"
                      onClick={() => setOpen(false)}
                    />
                    <MenuItem
                      to="/artisan/portfolio"
                      icon={<Briefcase size={16} />}
                      label="My Portfolio"
                      onClick={() => setOpen(false)}
                    />
                  </>
                )}
                <div className="lg:hidden">
                  {activeRole === 'user' && (
                    <MenuItem
                      to="/user/my-favorites"
                      icon={<Bookmark size={16} />}
                      label="My Favorites"
                      onClick={() => setOpen(false)}
                    />
                  )}
                </div>

                <MenuItem
                  to={
                    activeRole === 'user'
                      ? '/user/settings'
                      : '/artisan/settings'
                  }
                  icon={<Settings size={16} />}
                  label="Settings"
                  onClick={() => setOpen(false)}
                />

                <MenuItem
                  to="/support"
                  icon={<HelpCircle size={16} />}
                  label="Help & Support"
                  onClick={() => setOpen(false)}
                />

                <div className="h-px bg-gray-200 my-2" />

                <button
                  onClick={handleLogout}
                  className="w-full flex items-center gap-3 px-3 py-2
                             text-red-600 hover:bg-red-50 rounded-lg transition"
                >
                  <LogOut size={16} />
                  <span className="text-sm font-medium">Logout</span>
                </button>
              </div>
            </div>
          )}
        </>
      ) : (
        <button
          className="
    flex items-center justify-center
    px-3 py-2 text-sm
    sm:px-4 sm:py-2
    lg:px-6 lg:py-2.5 lg:text-base

    border-2 border-rose-600 text-rose-700
    font-semibold rounded-lg
    hover:bg-rose-600 hover:text-white
    transition-all
    ml-1
  "
          onClick={openSignin}
        >
          <User className="w-4 h-4 lg:w-5 lg:h-5" />
          <span className="hidden sm:inline ml-2">Sign In</span>
        </button>
      )}
    </div>
  );
}

/* ----- reusable menu item ----- */
function MenuItem({ to, icon, label, onClick }) {
  return (
    <Link to={to} onClick={onClick}>
      <div
        className="flex items-center gap-3 px-3 py-2
                      text-gray-700 hover:bg-gray-100 rounded-lg transition"
      >
        {icon}
        <span className="text-sm font-medium">{label}</span>
      </div>
    </Link>
  );
}

export default Account;
